import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Lock,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  Shield,
  Target,
  Users,
  DollarSign,
  Globe,
  CheckCircle2,
  AlertTriangle,
  Rocket,
  Building2,
  PieChart,
  BarChart3,
  Briefcase,
  Award,
  X,
  Heart,
} from 'lucide-react';

// SECURITY: Investor password is validated server-side ONLY
// NO fallback passwords - fail closed if backend unavailable
const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const STORAGE_KEY = 'fth_investor_access';

const InvestorPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check localStorage for existing auth
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'true') {
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!password.trim()) {
      setError('Please enter a password.');
      return;
    }
    
    try {
      // Validate via backend - NO FALLBACK
      const response = await fetch(`${API_URL}/api/config/investor-access/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.valid) {
          localStorage.setItem(STORAGE_KEY, 'true');
          setIsAuthenticated(true);
        } else {
          setError('Invalid password. Please contact us for access.');
        }
      } else if (response.status === 503) {
        // Service unavailable - investor access not configured
        setError('Investor access is not currently available. Please contact us.');
      } else {
        setError('Unable to verify password. Please try again later.');
      }
    } catch (e) {
      // FAIL CLOSED - no fallback passwords
      setError('Connection error. Please try again later or contact support.');
    }
  };

  const slides = [
    // Slide 1: Title
    {
      id: 'title',
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mb-8">
            <Heart className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-5xl font-bold text-foreground mb-4">FundTheHope</h1>
          <p className="text-2xl text-emerald-400 mb-8">5-Year Business Plan</p>
          <p className="text-xl text-muted-foreground">Investor Presentation</p>
          <div className="mt-12 flex gap-4">
            <Badge className="bg-emerald-500/20 text-emerald-400 px-4 py-2 text-sm">
              <Shield className="w-4 h-4 mr-2" /> Trust-First Platform
            </Badge>
            <Badge className="bg-blue-500/20 text-blue-400 px-4 py-2 text-sm">
              <Globe className="w-4 h-4 mr-2" /> Global Scale
            </Badge>
            <Badge className="bg-purple-500/20 text-purple-400 px-4 py-2 text-sm">
              <TrendingUp className="w-4 h-4 mr-2" /> $5B+ GMV Target
            </Badge>
          </div>
        </div>
      ),
    },
    // Slide 2: Executive Summary
    {
      id: 'executive-summary',
      content: (
        <div className="h-full">
          <h2 className="text-3xl font-bold text-foreground mb-8 flex items-center gap-3">
            <Briefcase className="w-8 h-8 text-emerald-400" /> Executive Summary
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-card/50 rounded-xl p-6 border border-border">
              <h3 className="text-xl font-semibold text-emerald-400 mb-4">What We Are</h3>
              <p className="text-muted-foreground leading-relaxed">
                FundTheHope is a global, trust-first crowdfunding platform enabling individuals, nonprofits, 
                teams, and project owners to raise funds transparently for any legitimate cause — medical, 
                nonprofit, sports, education, disaster relief, innovation, and community projects.
              </p>
            </div>
            <div className="bg-card/50 rounded-xl p-6 border border-border">
              <h3 className="text-xl font-semibold text-emerald-400 mb-4">Core Differentiators</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Verification</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Secure payment processing</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Milestone-driven accountability</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Transparency tools for donors</li>
              </ul>
            </div>
          </div>
          <div className="mt-8 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-xl p-6 border border-emerald-500/30">
            <h3 className="text-xl font-semibold text-foreground mb-4">5-Year Vision</h3>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-emerald-400">Top 5</p>
                <p className="text-sm text-muted-foreground">Global Crowdfunding Platform</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-emerald-400">$5B+</p>
                <p className="text-sm text-muted-foreground">GMV Annually by Year 5</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-emerald-400">Year 2-3</p>
                <p className="text-sm text-muted-foreground">Profitable</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-emerald-400">IPO</p>
                <p className="text-sm text-muted-foreground">Ready Candidate</p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    // Slide 3: Problem Statement
    {
      id: 'problem',
      content: (
        <div className="h-full">
          <h2 className="text-3xl font-bold text-foreground mb-8 flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-red-400" /> Problem Statement
          </h2>
          <p className="text-xl text-muted-foreground mb-8">Crowdfunding today is broken in 4 ways:</p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-red-500/10 rounded-xl p-6 border border-red-500/30">
              <h3 className="text-xl font-semibold text-red-400 mb-3">1. Low Trust</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Fake campaigns</li>
                <li>• No proof of fund usage</li>
                <li>• Poor accountability</li>
              </ul>
            </div>
            <div className="bg-orange-500/10 rounded-xl p-6 border border-orange-500/30">
              <h3 className="text-xl font-semibold text-orange-400 mb-3">2. Funds Released Upfront</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• No milestone enforcement</li>
                <li>• Money disappears</li>
                <li>• Zero accountability</li>
              </ul>
            </div>
            <div className="bg-yellow-500/10 rounded-xl p-6 border border-yellow-500/30">
              <h3 className="text-xl font-semibold text-yellow-400 mb-3">3. Donor Fatigue</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Emotional manipulation</li>
                <li>• Little post-donation engagement</li>
                <li>• No impact visibility</li>
              </ul>
            </div>
            <div className="bg-purple-500/10 rounded-xl p-6 border border-purple-500/30">
              <h3 className="text-xl font-semibold text-purple-400 mb-3">4. Platform Greed</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• High, opaque fees</li>
                <li>• Weak user protections</li>
                <li>• Limited transparency</li>
              </ul>
            </div>
          </div>
          <p className="mt-8 text-center text-lg text-muted-foreground">
            This creates massive regulatory, reputational, and user trust gaps.
          </p>
        </div>
      ),
    },
    // Slide 4: Solution
    {
      id: 'solution',
      content: (
        <div className="h-full">
          <h2 className="text-3xl font-bold text-foreground mb-4 flex items-center gap-3">
            <Shield className="w-8 h-8 text-emerald-400" /> FundTheHope Solution
          </h2>
          <p className="text-xl text-emerald-400 mb-8 italic">
            &quot;The most trusted fundraising platform for real-world impact.&quot;
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-card/50 rounded-xl p-6 border border-border text-center">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Verified Campaigns</h3>
              <p className="text-sm text-muted-foreground">KYC/KYB verification for all creators</p>
            </div>
            <div className="bg-card/50 rounded-xl p-6 border border-border text-center">
              <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Escrow-Based</h3>
              <p className="text-sm text-muted-foreground">Funds held safely until milestones met</p>
            </div>
            <div className="bg-card/50 rounded-xl p-6 border border-border text-center">
              <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Milestone-Driven</h3>
              <p className="text-sm text-muted-foreground">Phased fund release with proof</p>
            </div>
            <div className="bg-card/50 rounded-xl p-6 border border-border text-center">
              <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-orange-400" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Trust Tools</h3>
              <p className="text-sm text-muted-foreground">Verification and transparency features for all users</p>
            </div>
            <div className="bg-card/50 rounded-xl p-6 border border-border text-center">
              <div className="w-16 h-16 bg-teal-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-8 h-8 text-teal-400" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Transparent Tracking</h3>
              <p className="text-sm text-muted-foreground">Real-time progress & impact updates</p>
            </div>
            <div className="bg-card/50 rounded-xl p-6 border border-border text-center">
              <div className="w-16 h-16 bg-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-pink-400" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Global-First</h3>
              <p className="text-sm text-muted-foreground">Multi-category, worldwide reach</p>
            </div>
          </div>
          <p className="mt-8 text-center text-lg font-semibold text-emerald-400">
            This is not GoFundMe 2.0 — it&apos;s a trust upgrade to crowdfunding.
          </p>
        </div>
      ),
    },
    // Slide 5: Business Model
    {
      id: 'business-model',
      content: (
        <div className="h-full">
          <h2 className="text-3xl font-bold text-foreground mb-8 flex items-center gap-3">
            <DollarSign className="w-8 h-8 text-emerald-400" /> Business Model
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-4">Revenue Streams</h3>
              <div className="space-y-4">
                <div className="bg-emerald-500/10 rounded-lg p-4 border border-emerald-500/30">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-emerald-400">Platform Fee (Primary)</span>
                    <span className="text-foreground font-bold">5-8%</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Per successful campaign</p>
                </div>
                <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/30">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-blue-400">Payment Processing</span>
                    <span className="text-foreground font-bold">1-2%</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Margin depending on region</p>
                </div>
                <div className="bg-purple-500/10 rounded-lg p-4 border border-purple-500/30">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-purple-400">Featured Campaigns</span>
                    <span className="text-foreground font-bold">Paid</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Boost visibility & reach</p>
                </div>
                <div className="bg-orange-500/10 rounded-lg p-4 border border-orange-500/30">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-orange-400">Subscription Plans</span>
                    <span className="text-foreground font-bold">Future</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">NGOs, institutions, repeat creators</p>
                </div>
                <div className="bg-teal-500/10 rounded-lg p-4 border border-teal-500/30">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-teal-400">Enterprise Partners</span>
                    <span className="text-foreground font-bold">Custom</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Corporates, foundations, sports bodies</p>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-4">Trust Builder</h3>
              <div className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-xl p-6 border border-emerald-500/30 h-[calc(100%-2rem)]">
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="w-8 h-8 text-emerald-400" />
                  <span className="text-xl font-bold text-foreground">No Fees on Failed Campaigns</span>
                </div>
                <p className="text-muted-foreground mb-6">
                  Unlike competitors, we only charge when campaigns succeed. This builds trust and 
                  aligns our incentives with campaign creators.
                </p>
                <div className="bg-card/50 rounded-lg p-4">
                  <p className="text-emerald-400 font-semibold">Key Insight:</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    This zero-risk model for creators dramatically increases platform adoption 
                    and creator loyalty, leading to higher lifetime value.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    // Slide 6: Market Opportunity
    {
      id: 'market',
      content: (
        <div className="h-full">
          <h2 className="text-3xl font-bold text-foreground mb-8 flex items-center gap-3">
            <Globe className="w-8 h-8 text-emerald-400" /> Market Opportunity
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-4">Market Size</h3>
              <div className="bg-card/50 rounded-xl p-6 border border-border">
                <div className="text-center mb-6">
                  <p className="text-5xl font-bold text-emerald-400">$18-25B</p>
                  <p className="text-muted-foreground mt-2">Global Crowdfunding Market</p>
                </div>
                <div className="flex items-center justify-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                  <span className="text-xl font-semibold text-foreground">15%+ CAGR</span>
                </div>
                <p className="text-center text-muted-foreground">
                  Donation-based crowdfunding is the largest segment
                </p>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-4">Our Competitive Edge</h3>
              <div className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-xl p-6 border border-emerald-500/30">
                <p className="text-lg text-foreground mb-4">We compete on what matters most:</p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                      <Shield className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">Trust</p>
                      <p className="text-sm text-muted-foreground">Verified campaigns & KYC</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">Governance</p>
                      <p className="text-sm text-muted-foreground">Milestone-driven accountability</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">Transparency</p>
                      <p className="text-sm text-muted-foreground">Real-time tracking & updates</p>
                    </div>
                  </div>
                </div>
                <p className="mt-4 text-sm text-emerald-400 font-semibold">
                  These are exactly what regulators and donors now demand.
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    // Slide 7: Competitive Landscape
    {
      id: 'competition',
      content: (
        <div className="h-full">
          <h2 className="text-3xl font-bold text-foreground mb-8 flex items-center gap-3">
            <Award className="w-8 h-8 text-emerald-400" /> Competitive Landscape
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-4 px-4 text-foreground font-semibold">Platform</th>
                  <th className="text-center py-4 px-4 text-foreground font-semibold">Verification</th>
                  <th className="text-center py-4 px-4 text-foreground font-semibold">Escrow</th>
                  <th className="text-center py-4 px-4 text-foreground font-semibold">Milestones</th>
                  <th className="text-center py-4 px-4 text-foreground font-semibold">Donor Protection</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border/50">
                  <td className="py-4 px-4 text-muted-foreground">GoFundMe</td>
                  <td className="py-4 px-4 text-center"><span className="text-red-400">❌ Limited</span></td>
                  <td className="py-4 px-4 text-center"><span className="text-red-400">❌</span></td>
                  <td className="py-4 px-4 text-center"><span className="text-red-400">❌</span></td>
                  <td className="py-4 px-4 text-center"><span className="text-red-400">❌</span></td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-4 px-4 text-muted-foreground">Kickstarter</td>
                  <td className="py-4 px-4 text-center"><span className="text-yellow-400">⚠️ Partial</span></td>
                  <td className="py-4 px-4 text-center"><span className="text-red-400">❌</span></td>
                  <td className="py-4 px-4 text-center"><span className="text-red-400">❌</span></td>
                  <td className="py-4 px-4 text-center"><span className="text-red-400">❌</span></td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-4 px-4 text-muted-foreground">Indiegogo</td>
                  <td className="py-4 px-4 text-center"><span className="text-yellow-400">⚠️ Partial</span></td>
                  <td className="py-4 px-4 text-center"><span className="text-red-400">❌</span></td>
                  <td className="py-4 px-4 text-center"><span className="text-red-400">❌</span></td>
                  <td className="py-4 px-4 text-center"><span className="text-red-400">❌</span></td>
                </tr>
                <tr className="bg-emerald-500/10">
                  <td className="py-4 px-4 font-bold text-emerald-400">FundTheHope</td>
                  <td className="py-4 px-4 text-center"><span className="text-emerald-400">✅ Strong</span></td>
                  <td className="py-4 px-4 text-center"><span className="text-emerald-400">✅</span></td>
                  <td className="py-4 px-4 text-center"><span className="text-emerald-400">✅</span></td>
                  <td className="py-4 px-4 text-center"><span className="text-emerald-400">✅</span></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-8 bg-card/50 rounded-xl p-6 border border-border">
            <p className="text-lg text-white">
              <span className="text-emerald-400 font-semibold">Our moat is governance, not UI.</span> While others 
              compete on features and virality, we compete on trust infrastructure — exactly what the 
              market is demanding as regulations tighten globally.
            </p>
          </div>
        </div>
      ),
    },
    // Slide 8: Financial Projections
    {
      id: 'financials',
      content: (
        <div className="h-full">
          <h2 className="text-3xl font-bold text-foreground mb-8 flex items-center gap-3">
            <PieChart className="w-8 h-8 text-emerald-400" /> 5-Year Financial Projections
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-4">Revenue Growth</h3>
              <div className="space-y-3">
                {[
                  { year: 'Year 1', gmv: '$25M', revenue: '$1.5M', color: 'emerald' },
                  { year: 'Year 2', gmv: '$120M', revenue: '$7.2M', color: 'teal' },
                  { year: 'Year 3', gmv: '$500M', revenue: '$30M', color: 'blue' },
                  { year: 'Year 4', gmv: '$1.8B', revenue: '$108M', color: 'purple' },
                  { year: 'Year 5', gmv: '$5.0B', revenue: '$300M', color: 'pink' },
                ].map((item, idx) => (
                  <div key={idx} className={`bg-${item.color}-500/10 rounded-lg p-4 border border-${item.color}-500/30`}>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-foreground">{item.year}</span>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">GMV: <span className="text-foreground">{item.gmv}</span></p>
                        <p className="text-lg font-bold text-emerald-400">{item.revenue}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-4">Key Assumptions</h3>
              <div className="bg-card/50 rounded-xl p-6 border border-border mb-6">
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    Average platform fee: 6%
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    Gradual regional expansion
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    Strong trust-led conversion
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    Conservative growth estimates
                  </li>
                </ul>
              </div>
              <div className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-xl p-6 border border-emerald-500/30">
                <p className="text-2xl font-bold text-foreground mb-2">Break-even</p>
                <p className="text-4xl font-bold text-emerald-400">Year 2-3</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Conservative path to profitability
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    // Slide 9: Go-To-Market Strategy
    {
      id: 'gtm',
      content: (
        <div className="h-full">
          <h2 className="text-3xl font-bold text-foreground mb-8 flex items-center gap-3">
            <Rocket className="w-8 h-8 text-emerald-400" /> Go-To-Market Strategy
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-card/50 rounded-xl p-6 border border-emerald-500/30">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                  <span className="text-emerald-400 font-bold">1-2</span>
                </div>
                <h3 className="text-lg font-semibold text-foreground">Trust & Proof</h3>
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Launch in 3-5 priority regions</li>
                <li>• Focus: Medical, Nonprofit, Disaster Relief</li>
                <li>• Partner with NGOs, Hospitals, Sports bodies</li>
                <li>• PR-led trust narrative</li>
                <li>• Build case studies</li>
              </ul>
            </div>
            <div className="bg-card/50 rounded-xl p-6 border border-blue-500/30">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <span className="text-blue-400 font-bold">3-4</span>
                </div>
                <h3 className="text-lg font-semibold text-foreground">Scale</h3>
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Influencer-backed verified campaigns</li>
                <li>• Corporate CSR partnerships</li>
                <li>• Mobile app launch</li>
                <li>• Localization (language, payments)</li>
                <li>• Expand to 20+ countries</li>
              </ul>
            </div>
            <div className="bg-card/50 rounded-xl p-6 border border-purple-500/30">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                  <span className="text-purple-400 font-bold">5</span>
                </div>
                <h3 className="text-lg font-semibold text-foreground">Leadership</h3>
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Institutional fundraising</li>
                <li>• Government partnerships</li>
                <li>• Foundation collaborations</li>
                <li>• API-based infrastructure</li>
                <li>• IPO readiness</li>
              </ul>
            </div>
          </div>
          <div className="mt-8 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-xl p-6 border border-emerald-500/30">
            <h3 className="text-xl font-semibold text-foreground mb-3">Campaign Categories</h3>
            <div className="flex flex-wrap gap-3">
              {['Medical & Emergencies', 'Nonprofits & NGOs', 'Sports & Talent', 'Education', 'Disaster Relief', 'Community Projects', 'Innovation'].map((cat, idx) => (
                <Badge key={idx} className="bg-white/10 text-white px-3 py-1">{cat}</Badge>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    // Slide 10: Funding & Exit
    {
      id: 'funding',
      content: (
        <div className="h-full">
          <h2 className="text-3xl font-bold text-foreground mb-8 flex items-center gap-3">
            <DollarSign className="w-8 h-8 text-emerald-400" /> Funding Strategy & Exit
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-4">Funding Rounds</h3>
              <div className="space-y-4">
                <div className="bg-emerald-500/10 rounded-xl p-6 border border-emerald-500/30">
                  <p className="text-sm text-emerald-400 font-semibold mb-1">Seed / Pre-A</p>
                  <p className="text-3xl font-bold text-foreground">$2-4M</p>
                </div>
                <div className="bg-blue-500/10 rounded-xl p-6 border border-blue-500/30">
                  <p className="text-sm text-blue-400 font-semibold mb-1">Series A</p>
                  <p className="text-3xl font-bold text-foreground">$10-15M</p>
                </div>
              </div>
              <h3 className="text-xl font-semibold text-foreground mt-6 mb-4">Use of Funds</h3>
              <div className="space-y-2">
                {[
                  { label: 'Product & Engineering', pct: 40, color: 'emerald' },
                  { label: 'Trust & Compliance', pct: 25, color: 'blue' },
                  { label: 'Marketing & Partnerships', pct: 20, color: 'purple' },
                  { label: 'Operations', pct: 10, color: 'orange' },
                  { label: 'Legal & Regulatory', pct: 5, color: 'teal' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-24 text-sm text-muted-foreground">{item.pct}%</div>
                    <div className="flex-1 bg-card/50 rounded-full h-6 overflow-hidden">
                      <div 
                        className={`h-full bg-${item.color}-500/50 rounded-full`}
                        style={{ width: `${item.pct}%` }}
                      ></div>
                    </div>
                    <div className="w-40 text-sm text-foreground">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-4">Exit Strategy</h3>
              <div className="space-y-4">
                <div className="bg-card/50 rounded-xl p-6 border border-border">
                  <div className="flex items-center gap-3 mb-2">
                    <Building2 className="w-6 h-6 text-emerald-400" />
                    <p className="font-semibold text-foreground">Strategic Acquisition</p>
                  </div>
                  <p className="text-sm text-muted-foreground">FinTech, payments, or Big Tech companies</p>
                </div>
                <div className="bg-card/50 rounded-xl p-6 border border-border">
                  <div className="flex items-center gap-3 mb-2">
                    <TrendingUp className="w-6 h-6 text-blue-400" />
                    <p className="font-semibold text-foreground">IPO</p>
                  </div>
                  <p className="text-sm text-muted-foreground">If GMV scale sustains trajectory</p>
                </div>
                <div className="bg-card/50 rounded-xl p-6 border border-border">
                  <div className="flex items-center gap-3 mb-2">
                    <Globe className="w-6 h-6 text-purple-400" />
                    <p className="font-semibold text-foreground">Foundation-Backed Platform</p>
                  </div>
                  <p className="text-sm text-muted-foreground">Global impact-focused structure</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    // Slide 11: Final
    {
      id: 'final',
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <h2 className="text-4xl font-bold text-foreground mb-8">Final Takeaway</h2>
          <div className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-2xl p-8 border border-emerald-500/30 max-w-2xl">
            <p className="text-xl text-white leading-relaxed mb-6">
              FundTheHope is <span className="text-emerald-400 font-bold">legitimate</span>, <span className="text-emerald-400 font-bold">needed</span>, and <span className="text-emerald-400 font-bold">scalable</span>.
            </p>
            <p className="text-lg text-muted-foreground">
              Investors will back us because we lean hard into <span className="text-foreground font-semibold">trust</span>, <span className="text-foreground font-semibold">verification</span>, and <span className="text-foreground font-semibold">governance</span> — 
              not emotional crowdfunding.
            </p>
          </div>
          <div className="mt-12 flex gap-6">
            <div className="text-center">
              <p className="text-4xl font-bold text-emerald-400">$5B+</p>
              <p className="text-sm text-muted-foreground">GMV Target</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-emerald-400">$300M</p>
              <p className="text-sm text-muted-foreground">Revenue Year 5</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-emerald-400">Top 5</p>
              <p className="text-sm text-muted-foreground">Global Platform</p>
            </div>
          </div>
          <div className="mt-12">
            <p className="text-muted-foreground">Contact us at</p>
            <a href="mailto:investors@fundthehope.com" className="text-xl text-emerald-400 font-semibold hover:underline">investors@fundthehope.com</a>
          </div>
        </div>
      ),
    },
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  // Password Gate
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-card rounded-2xl p-8 border border-border shadow-xl">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">Investor Access</h1>
              <p className="text-muted-foreground">
                This section is protected. Please enter the password to view our business plan.
              </p>
            </div>
            <form onSubmit={handlePasswordSubmit}>
              <div className="space-y-4">
                <Input
                  type="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-secondary/50"
                />
                {error && (
                  <p className="text-red-400 text-sm">{error}</p>
                )}
                <Button type="submit" className="w-full bg-gradient-to-r from-emerald-500 to-teal-600">
                  Access Investor Deck
                </Button>
              </div>
            </form>
            <div className="mt-6 text-center">
              <Button variant="ghost" onClick={() => navigate('/')} className="text-muted-foreground">
                <ChevronLeft className="w-4 h-4 mr-2" /> Back to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Investor Deck
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 bg-card/80 backdrop-blur-lg border-b border-border z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-muted-foreground">
              <X className="w-4 h-4 mr-2" /> Exit
            </Button>
            <span className="text-muted-foreground">|</span>
            <span className="text-foreground font-semibold">FundTheHope Investor Deck</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-muted-foreground text-sm">
              Slide {currentSlide + 1} of {slides.length}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={prevSlide}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={nextSlide}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Slide Content */}
      <div className="pt-20 pb-24 min-h-screen">
        <div className="max-w-6xl mx-auto px-8 py-8">
          {slides[currentSlide].content}
        </div>
      </div>

      {/* Slide Navigation Dots */}
      <div className="fixed bottom-0 left-0 right-0 bg-card/80 backdrop-blur-lg border-t border-border py-4">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center gap-2">
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              className={`w-3 h-3 rounded-full transition-all ${
                idx === currentSlide 
                  ? 'bg-emerald-500 w-8' 
                  : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Keyboard Navigation */}
      <div className="hidden">
        {/* This is handled by useEffect below */}
      </div>
    </div>
  );
};

export default InvestorPage;
